import React from 'react';
import { Alert } from '@odin-labs/components';
import {
  ReviewCompletedProfiledTabProps,
  SelfOnboardingStepKey,
} from 'containers/selfOnboarding/types';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import { getDocumentInputsConfig } from 'containers/selfOnboarding/steps/documentStep';
import { DocumentKey } from 'graphql/types/enums';

/**
 * This component displays alerts for missing documents
 * in the review completed profile documents tab.
 */
export function ReviewCompletedProfileDocumentsAlerts(
  props: ReviewCompletedProfiledTabProps,
): React.ReactElement {
  const { state, navigation, localize } = props;
  const { documents, documentSteps, documentTypes } = state;
  const { goToStep } = navigation;

  const isDocumentMissing = (documentKey: string): boolean =>
    !documents?.some(({ key }) => key === documentKey);
  const missingDocuments = documentSteps
    .filter(({ key }) => isDocumentMissing(key))
    .map(({ key, workerDocTypeKey }) => {
      const { missingCopy, missingMultiInstanceCopy } =
        getDocumentInputsConfig(workerDocTypeKey) ?? {};
      return {
        docKey: key,
        workerDocTypeKey,
        missingText: localize(missingMultiInstanceCopy ?? missingCopy, {
          videoTitle: documentTypes[key]?.multiInstanceDocTitle,
        }),
      };
    });

  if (!missingDocuments.length) {
    return null;
  }

  const getActionTextCopy = (workerDocTypeKey: DocumentKey): Copy => {
    switch (workerDocTypeKey) {
      case DocumentKey.JobsiteSafetyVideo:
        return Copy.watch;
      case DocumentKey.JobsiteSafetyDocument:
        return Copy.read;
      case DocumentKey.WorkerConsentDocument:
        return Copy.acknowledge;
      default:
        return Copy.add;
    }
  };

  return (
    <div className="odin-space-y-5">
      {missingDocuments.map(({ docKey, missingText, workerDocTypeKey }) => (
        <Alert
          key={docKey}
          text={missingText}
          actionText={localize(getActionTextCopy(workerDocTypeKey))}
          onAction={(): void => goToStep(docKey as SelfOnboardingStepKey)}
        />
      ))}
    </div>
  );
}
